import { hasInventoryCountPermission, hasPurchasingStockPermission } from "../utils/purchasingStockPermissions";
/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { NavLink, useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";
import { hasAnyPurchasingSettingsAccess } from "../utils/purchasingSettingsPermissions";

function NavbarPurchasing() {
  const { languageId } = usePmsTheme();
  const currentUser = JSON.parse(localStorage.getItem("palantir_user"));
  const permissions = currentUser?.permissions || {}; // 🆕 permisos actuales del usuario
  const isAdminRole = Number(currentUser?.id) === 1;
  const canRequestServices = isAdminRole || permissions["purchasing.services.requests"] === true;
  const canApproveServices = isAdminRole || permissions["purchasing.services.approvals"] === true;
  const canConfigureServices = isAdminRole || permissions["purchasing.services.configure"] === true;
  const canViewServices = canRequestServices || canApproveServices;
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [recipesOpen, setRecipesOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [stockOrdersOpen, setStockOrdersOpen] = useState(false);
  const canCreateMarketplace = isAdminRole || permissions["purchasing.requisitions.create"] === true;

  // ✅ Determinar la Business Unit activa
  const businessUnitId =
    searchParams.get("business_unit_id") ||
    currentUser?.active_business_unit_id ||
    "1";

  const [businessUnitName, setBusinessUnitName] = useState("");

  // ✅ Cargar nombre de la BU
  useEffect(() => {
    const loadBusinessUnitName = async () => {
      for (let i = 0; i < 10; i++) {
        const token = localStorage.getItem("palantir_token");
        if (token) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (res.data?.name) setBusinessUnitName(res.data.name);
      } catch (err) {
        console.warn("❌ Error cargando unidad de negocio:", err);
      }
    };

    if (businessUnitId) loadBusinessUnitName();
  }, [businessUnitId]);

  // 🎨 Estilos (idénticos a PMS)
  const linkStyle = {
    color: "#fff",
    fontWeight: "bold",
    textDecoration: "none",
    padding: "10px 18px",
    borderRadius: "6px",
    fontSize: "1rem",
    transition: "background 0.3s ease, color 0.3s ease",
  };

  const activeLinkStyle = {
    background: "#000",
    color: "#fff",
  };

  // ✅ Solo pestañas principales del módulo Purchasing
  const canAccessPurchasing =
    isAdminRole ||
    Object.entries(permissions).some(([key, enabled]) => key.startsWith("purchasing.") && enabled === true) ||
    canViewServices ||
    canConfigureServices ||
    permissions["access_settings"] ||
    permissions["purchasing.orders.view_tab"] ||
    permissions["purchasing.requisitions.view_tab"] || // 👈 añade esto
    permissions["purchasing.receivings.view_tab"] ||   // 👈 y ya de paso
    permissions["purchasing.delivery_notes.view_tab"] || // 👈 para futuras pestañas
    permissions["purchasing.suppliers.view"] ||
    permissions["purchasing.items.view"] ||
    permissions["purchasing.recipes.view"] ||
    permissions["purchasing.reports.view"] ||
    permissions["purchasing.reports.items_purchased.view"] ||
    permissions["purchasing.settings.view"] ||
    hasAnyPurchasingSettingsAccess(currentUser);



  // 🚫 Si el usuario no tiene permisos, mostrar aviso
  if (!canAccessPurchasing) {
    return (
      <div
        style={{
          color: "white",
          textAlign: "center",
          marginTop: "4rem",
          fontSize: "1.2rem",
        }}
      >
        🚫 You don't have permission to access the Purchasing module.
      </div>
    );
  }

  // ✅ Control granular de pestañas según permisos
  const canViewOrders =
    isAdminRole ||
    permissions["purchasing.orders.view_tab"] ||
    permissions["purchasing.requisitions.view_tab"];

  const canViewSuppliers = isAdminRole || permissions["purchasing.suppliers.view"] === true;
  const canViewItems = isAdminRole || ["purchasing.items.view", "purchasing.categories.view", "purchasing.equivalents.view"]
    .some((key) => permissions[key] === true);
  const catalogEntryPath = isAdminRole || permissions["purchasing.items.view"] === true || permissions["purchasing.categories.view"] === true
    ? "/purchasing/items" : "/purchasing/items/equivalent-products";
  const canViewRecipes = hasPurchasingStockPermission(currentUser, "purchasing.recipes.view");
  const canViewMenu = hasPurchasingStockPermission(currentUser, "purchasing.menu.view");
  const canViewReports =
    isAdminRole ||
    permissions["purchasing.reports.items_purchased.view"] === true;
  const canViewGeneralSettings = isAdminRole || permissions["purchasing.settings.view"];
  const canViewSettings = canViewGeneralSettings || canConfigureServices ||
    hasAnyPurchasingSettingsAccess(currentUser);
  const canViewStockManagement = hasInventoryCountPermission(currentUser, "view") ||
    ["current", "current.snapshot_report", "current.accounting_closures.view", "requisitions", "dispatched", "returns", "transfers"].some((key) =>
      hasPurchasingStockPermission(currentUser, `purchasing.stock.${key}`));
  const settingsEntryPath = canViewGeneralSettings && !canConfigureServices && !hasAnyPurchasingSettingsAccess(currentUser)
    ? "/purchasing/settings/appearance" : "/purchasing/settings";


  return (
    <nav
      className="navbar navbar-expand-lg navbar-dark fixed-top"

      style={{
        background: "linear-gradient(to right, #5B2C6F, #2C3E50, #000)",
        padding: "10px 0",
        width: "98.7%",
        minHeight: "56px",
      }}
    >
      <div className="container-fluid">
        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-controls="navbarPurchasing"
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          className={`collapse navbar-collapse ${menuOpen ? "show" : ""}`}
          id="navbarPurchasing"
        >
          <ul className="navbar-nav gap-4 align-items-center">

            
            {/* 🟢 DASHBOARD TAB */}
            <li className="nav-item">
              <NavLink
                to={`/purchasing/dashboard?business_unit_id=${businessUnitId}`}
                onClick={() => setMenuOpen(false)}
                style={({ isActive }) =>
                  isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                }
              >
                Dashboard
              </NavLink>
            </li>

            {(canViewOrders || canCreateMarketplace) && (
              <li className="nav-item dropdown">
                <NavLink
                  to={`${canViewOrders ? "/purchasing/orders" : "/purchasing/orders/marketplace"}?business_unit_id=${businessUnitId}`}
                  onClick={() => setMenuOpen(false)}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Stock Orders
                </NavLink>
                {canCreateMarketplace && <>
                  <button type="button" aria-label="Stock Orders menu" aria-expanded={stockOrdersOpen} aria-controls="purchasing-stock-orders-menu"
                    onClick={() => setStockOrdersOpen((open) => !open)} style={{ border: 0, background: "transparent", color: "white" }}>▾</button>
                  {stockOrdersOpen && <ul id="purchasing-stock-orders-menu" className="dropdown-menu show">
                    <li><NavLink className="dropdown-item" to={`/purchasing/orders/marketplace?business_unit_id=${businessUnitId}`}
                      onClick={() => { setStockOrdersOpen(false); setMenuOpen(false); }}>Marketplace</NavLink></li>
                    {canViewOrders && <li><NavLink className="dropdown-item" to={`/purchasing/orders?business_unit_id=${businessUnitId}`}
                      onClick={() => { setStockOrdersOpen(false); setMenuOpen(false); }}>Stock Orders</NavLink></li>}
                  </ul>}
                </>}
              </li>
            )}

            {canViewServices && (
              <li className="nav-item">
                <NavLink
                  to={`/purchasing/service-orders?business_unit_id=${businessUnitId}`}
                  onClick={() => setMenuOpen(false)}
                  style={({ isActive }) =>
                    isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                  }
                >
                  Service Orders
                </NavLink>
              </li>
            )}

            
            {canViewStockManagement && <li className="nav-item">
              <NavLink
                to={`/purchasing/stock-management?business_unit_id=${businessUnitId}`}
                onClick={() => setMenuOpen(false)}
                style={({ isActive }) =>
                  isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                }
              >
                Stock Management
              </NavLink>
            </li>}


          {canViewSuppliers && (
            <li className="nav-item">
              <NavLink
                to={`/purchasing/suppliers?business_unit_id=${businessUnitId}`}
                onClick={() => setMenuOpen(false)}
                style={({ isActive }) =>
                  isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                }
              >
                Suppliers
              </NavLink>
            </li>
          )}

          {canViewItems && (
            <li className="nav-item">
              <NavLink
                to={`${catalogEntryPath}?business_unit_id=${businessUnitId}`}
                onClick={() => setMenuOpen(false)}
                style={({ isActive }) =>
                  isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                }
              >
                Items & Families
              </NavLink>
            </li>
          )}

          {(canViewRecipes || canViewMenu) && (
            <li className="nav-item dropdown" onKeyDown={(event) => {
              if (event.key === "Escape") {
                setRecipesOpen(false);
                event.currentTarget.querySelector("button")?.focus();
              }
            }}>
              <button
                type="button"
                className="dropdown-toggle"
                aria-expanded={recipesOpen}
                aria-controls="purchasing-recipe-navigation"
                onClick={() => setRecipesOpen((open) => !open)}
                style={{ ...linkStyle, border: 0, background: "transparent" }}
              >
                {languageId === "es" ? "Recetas y costes" : "Recipes & Costing"}
              </button>
              {recipesOpen && <ul id="purchasing-recipe-navigation" className="dropdown-menu show">
                {[["", "Recipes", "Recetas", canViewRecipes], ["/menu", "Menu & Margins", "Carta y márgenes", canViewMenu]].filter((entry) => entry[3]).map(([suffix, label, translated]) => (
                  <li key={label}>
                    <NavLink
                      to={`/purchasing/recipes${suffix}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`}
                      end
                      className={({ isActive }) => `dropdown-item${isActive ? " active" : ""}`}
                      onClick={() => { setMenuOpen(false); setRecipesOpen(false); }}
                    >
                      {languageId === "es" ? translated : label}
                    </NavLink>
                  </li>
                ))}
              </ul>}
            </li>
          )}

          {canViewReports && (
            <li className="nav-item dropdown">
              <button type="button" className="nav-link dropdown-toggle" style={{ ...linkStyle, border: 0, background: "transparent" }}
                aria-expanded={reportsOpen} aria-controls="purchasing-reports-menu" onClick={() => setReportsOpen((value) => !value)}>
                {languageId === "es" ? "Informes" : "Reports"}
              </button>
              {reportsOpen && <ul id="purchasing-reports-menu" className="dropdown-menu show">
                {[["items-purchased", "Items Purchased", "Artículos comprados"], ["item-last-purchase", "Item last purchase", "Última compra por artículo"], ["item-last-unit-cost", "Last unit cost", "Último coste unitario"], ["equivalent-products", "Equivalent product comparison", "Comparativa de productos equivalentes"]].map(([suffix, label, translated]) => <li key={suffix}>
                  <NavLink to={`/purchasing/reports/${suffix}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`}
                    className={({ isActive }) => `dropdown-item${isActive ? " active" : ""}`} onClick={() => { setMenuOpen(false); setReportsOpen(false); }}>
                    {languageId === "es" ? translated : label}
                  </NavLink>
                </li>)}
              </ul>}
            </li>
          )}

          {canViewSettings && (
            <li className="nav-item">
              <NavLink
                to={`${settingsEntryPath}?business_unit_id=${businessUnitId}`}
                onClick={() => setMenuOpen(false)}
                style={({ isActive }) =>
                  isActive ? { ...linkStyle, ...activeLinkStyle } : linkStyle
                }
              >
                Settings
              </NavLink>
            </li>
          )}

          </ul>
        </div>
      </div>

      {/* 🔹 Panel derecho con BU y logout */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "2px",
        }}
      >
        {businessUnitName && (
          <div
            style={{
              color: "#fff",
              fontSize: "0.8rem",
              fontWeight: "bold",
              maxWidth: "250px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={businessUnitName}
          >
            {businessUnitName}
          </div>
        )}

        <button
          onClick={() => {
            localStorage.removeItem("palantir_token");
            localStorage.removeItem("palantir_user");
            window.location.href = "/";
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "0.8rem",
            cursor: "pointer",
            opacity: 0.6,
          }}
          title="Logout"
        >
          Logout
        </button>
      </div>
    </nav>
  );
}

export default NavbarPurchasing;
